import {
  HoverCard,
  HoverCardContent,
  HoverCardPortal,
  HoverCardTrigger,
} from "@radix-ui/react-hover-card";
import type { ReactNode } from "react";

import { formatGitSha, pluralize, truncateCommitMessage } from "~/utils";
import { formatRelativeUtc } from "~/utils/time";

import { CopyButton } from "./CopyButton";
import { GitButton } from "./GitButton";
import { Icon } from "./icon";

const COMMIT_URL = "https://github.com/turbopuffer/turbopuffer/commit";
const CLUSTER_CAP = 8;

interface CommitDetails {
  sha: string;
  message: string;
  date?: string | null;
}

export function CommitPopover({
  commit,
  onClusters = [],
  align = "start",
  children,
}: {
  commit: CommitDetails;
  onClusters?: string[];
  align?: "start" | "end";
  children: ReactNode;
}) {
  const short = formatGitSha(commit.sha);
  const extraClusters = Math.max(0, onClusters.length - CLUSTER_CAP);

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardPortal>
        <HoverCardContent
          role="tooltip"
          aria-label={`${short} commit details`}
          align={align}
          sideOffset={4}
          className="z-50 w-max max-w-xs rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-left font-sans text-gray-900 shadow-md"
        >
          <div className="flex items-center gap-2">
            <Icon
              name="GitCommitHorizontal"
              size={16}
              className="text-gray-600"
            />
            <span className="font-mono text-sm font-medium">{short}</span>
            <CopyButton value={commit.sha} label="Copy full SHA" size={16} />
            <GitButton
              icon="GitCompare"
              size={16}
              title="View commit on GitHub"
              href={`${COMMIT_URL}/${commit.sha}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          </div>
          {commit.date ? (
            <p className="mt-1 text-xs text-gray-500">
              {formatRelativeUtc(new Date(commit.date), new Date())}
            </p>
          ) : null}
          <p className="mt-1 break-words text-sm text-gray-700">
            {truncateCommitMessage(commit.message)}
          </p>
          {onClusters.length > 0 ? (
            <>
              <p className="mt-3 text-xs text-gray-500">
                on {pluralize(onClusters.length, "cluster")}
              </p>
              <ul className="mt-1 font-mono text-xs text-gray-700">
                {onClusters.slice(0, CLUSTER_CAP).map((id) => (
                  <li key={id}>{id}</li>
                ))}
                {extraClusters > 0 ? (
                  <li className="text-gray-500">+{extraClusters} more</li>
                ) : null}
              </ul>
            </>
          ) : null}
        </HoverCardContent>
      </HoverCardPortal>
    </HoverCard>
  );
}
